import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X } from 'lucide-react';
import { siteData } from '../mock/data';
import LogoMark from './LogoMark';

const Header = () => {
  const { nav, company } = siteData;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out ${
          scrolled ? 'py-4 backdrop-blur-md bg-[#F5F1EA]/85 border-b border-[#0A2A28]/8' : 'py-7 bg-transparent'
        }`}
      >
        <div className="container-x flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <LogoMark size={42} className="transition-transform duration-500 group-hover:scale-105" />
            <div className="flex flex-col leading-none">
              <span className="font-display text-[22px] md:text-[24px] text-[#0A2A28] tracking-tight">
                Aurelian <span className="font-serif-italic text-[#BFA070]">Heights</span>
              </span>
              <span className="font-mono-up text-[9px] text-[#0A2A28]/55 mt-1 hidden sm:inline">Luxusimmobilienverwaltung</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {nav.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`link-underline text-[13px] tracking-[0.18em] uppercase font-medium transition-colors ${
                    active ? 'text-[#BFA070]' : 'text-[#0A2A28] hover:text-[#BFA070]'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={`tel:${company.phoneHref}`}
              className="hidden md:flex items-center gap-2 text-[#0A2A28] link-underline text-[12px] tracking-[0.18em] uppercase font-medium"
            >
              <Phone size={14} strokeWidth={1.5} />
              <span>{company.phone}</span>
            </a>
            <button
              aria-label="Menü öffnen"
              onClick={() => setOpen(true)}
              className="lg:hidden w-11 h-11 flex items-center justify-center border border-[#0A2A28]/25 hover:border-[#BFA070] hover:text-[#BFA070] transition-colors"
            >
              <Menu size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div className={`fixed inset-0 z-[60] pointer-events-none ${open ? 'pointer-events-auto' : ''}`}>
        <div
          className={`absolute inset-0 bg-[#0A2A28]/40 backdrop-blur-sm transition-opacity duration-500 ${open ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setOpen(false)}
        />
        <aside
          className={`absolute right-0 top-0 h-full w-full max-w-md bg-[#0A2A28] text-[#E8E2D5] p-10 transition-transform duration-700 ease-[cubic-bezier(0.77,0,0.175,1)] ${open ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <div className="flex items-center justify-between mb-16">
            <div className="flex items-center gap-3">
              <LogoMark size={36} />
              <span className="font-display text-2xl">Aurelian <span className="font-serif-italic text-[#BFA070]">Heights</span></span>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Menü schließen"
              className="w-11 h-11 flex items-center justify-center border border-[#E8E2D5]/30 hover:border-[#BFA070] hover:text-[#BFA070] transition-colors"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
          </div>
          <nav className="flex flex-col gap-6">
            {nav.map((item, i) => (
              <Link
                key={item.path}
                to={item.path}
                className="group flex items-baseline gap-4"
              >
                <span className="lux-num">0{i + 1}</span>
                <span className="font-display text-4xl md:text-5xl group-hover:text-[#BFA070] transition-colors">
                  {item.label}
                </span>
              </Link>
            ))}
          </nav>
          <div className="mt-16 pt-8 border-t border-[#E8E2D5]/15 space-y-3">
            <a href={`tel:${company.phoneHref}`} className="block link-underline text-sm tracking-[0.18em] uppercase">
              {company.phone}
            </a>
            <a href={`mailto:${company.email}`} className="block link-underline text-sm tracking-[0.18em] uppercase">
              {company.email}
            </a>
          </div>
        </aside>
      </div>
    </>
  );
};

export default Header;
